import React, { Dispatch, SetStateAction } from 'react';
import { Grid, GridItem, Text, FormControl, FormLabel, Select, Input } from '@chakra-ui/react';
import { ParcelInfo } from 'src/api/v1-api';

interface ParcelInfoFormProps {
  parcelInfo: ParcelInfo;
  setParcelInfo: Dispatch<SetStateAction<ParcelInfo>>;
}

const ParcelInfoForm: React.FC<ParcelInfoFormProps> = ({ parcelInfo, setParcelInfo }) => {
  const handleNumericChange = (field: keyof ParcelInfo, value: string) => {
    if (value === '') {
      setParcelInfo({
        ...parcelInfo,
        [field]: undefined,
      });
    } else {
      const numericValue = parseFloat(value);
      setParcelInfo({
        ...parcelInfo,
        [field]: isNaN(numericValue) ? undefined : numericValue,
      });
    }
  };

  return (
    <>
      <Text fontSize="md" fontWeight={600} mb={{ base: 3, md: 4 }}>
        Parcel Info
      </Text>

      {/* Dimensions Section */}
      <Grid
        width="full"
        templateColumns="repeat(4, 1fr)"
        gap={{ base: 3, md: 5 }}
        mb={{ base: 4, md: 6 }}
      >
        <GridItem colSpan={4}>
          <Text fontSize="sm" fontWeight={500} color="gray.600">
            Dimensions
          </Text>
        </GridItem>
        <GridItem colSpan={{ base: 4, md: 1 }}>
          <FormControl>
            <FormLabel>Distance Unit</FormLabel>
            <Select
              name="distance_unit"
              placeholder="Distance Unit"
              value={parcelInfo?.distance_unit ?? 'in'}
              onChange={(e) => setParcelInfo({ ...parcelInfo, distance_unit: e.target.value })}
              isRequired
            >
              <option value="in">inches</option>
              <option value="ft">feet</option>
              <option value="cm">centimeters</option>
              <option value="m">meters</option>
            </Select>
          </FormControl>
        </GridItem>
        <GridItem colSpan={{ base: 4, md: 1 }}>
          <FormControl>
            <FormLabel>Length</FormLabel>
            <Input
              type="number"
              name="length"
              placeholder="Length"
              value={parcelInfo?.length ?? ''}
              onChange={(e) => handleNumericChange('length', e.target.value)}
              min={0}
              step="0.01"
              isRequired
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={{ base: 4, md: 1 }}>
          <FormControl>
            <FormLabel>Width</FormLabel>
            <Input
              type="number"
              name="width"
              placeholder="Width"
              value={parcelInfo?.width ?? ''}
              onChange={(e) => handleNumericChange('width', e.target.value)}
              min={0}
              step="0.01"
              isRequired
            />
          </FormControl>
        </GridItem>
        <GridItem colSpan={{ base: 4, md: 1 }}>
          <FormControl>
            <FormLabel>Height</FormLabel>
            <Input
              type="number"
              name="height"
              placeholder="Height"
              value={parcelInfo?.height ?? ''}
              onChange={(e) => handleNumericChange('height', e.target.value)}
              min={0}
              step="0.01"
              isRequired
            />
          </FormControl>
        </GridItem>
      </Grid>

      {/* Weight Section */}
      <Grid
        width="full"
        templateColumns="repeat(2, 1fr)"
        gap={{ base: 3, md: 5 }}
        mb={{ base: 4, md: 6 }}
      >
        <GridItem colSpan={2}>
          <Text fontSize="sm" fontWeight={500} color="gray.600">
            Weight
          </Text>
        </GridItem>
        <GridItem colSpan={{ base: 2, md: 1 }}>
          <FormControl>
            <FormLabel>Weight Unit</FormLabel>
            <Select
              name="mass_unit"
              placeholder="Weight Unit"
              value={parcelInfo?.mass_unit ?? 'lb'}
              onChange={(e) => setParcelInfo({ ...parcelInfo, mass_unit: e.target.value })}
              isRequired
            >
              <option value="lb">pounds</option>
              <option value="oz">ounces</option>
            </Select>
          </FormControl>
        </GridItem>
        <GridItem colSpan={{ base: 2, md: 1 }}>
          <FormControl>
            <FormLabel>Weight</FormLabel>
            <Input
              type="number"
              min="0"
              step="0.01"
              name="weight"
              placeholder="Weight"
              value={parcelInfo?.weight ?? ''}
              onChange={(e) => handleNumericChange('weight', e.target.value)}
              isRequired
            />
          </FormControl>
        </GridItem>
      </Grid>

      <Text fontSize={'sm'} fontWeight={600} my={{ base: 4, lg: 6 }}>
        To ensure the success of your shipment, please add an accurate weight for the item.
      </Text>
    </>
  );
};

export default ParcelInfoForm;
